import { Device } from "./Device";
import { DeviceOrientation } from "./DeviceOrientation";

/**
 * Wrapper class over PIXI.Container that supports orientation changes.
 */
export abstract class MobileView extends PIXI.Container {

    static dispose(): void {
        window.removeEventListener("orientationchange", MobileView.onOrientationChange);
        MobileView._instances.length = 0;
        MobileView._isListening = false;
    }

    /**
     * Returns Device Orientation. Portrait or Landscape
     * @returns {com.egt.library.DeviceOrientation}
     */
    static get deviceOrientation(): DeviceOrientation {
        return Device.currentOrientation;
    }

    /**
     * Attaches orientationchange listener. Invoked in class constructor.
     */
    protected static listenForOrientationChange(): void {
        MobileView._currentOrientation = Device.currentOrientation;
        window.addEventListener("orientationchange", MobileView.onOrientationChange);
    }

    /**
     * Changes _currentOrientation
     * Invoked on orientationChange event (listenForOrientationChange)
     */
    protected static onOrientationChange() {
        const deviceOrientation = MobileView.deviceOrientation;

        if (deviceOrientation !== MobileView._currentOrientation) {
            MobileView._currentOrientation = deviceOrientation;

            MobileView.callOrientationChange();
            return;
        }
    }

    /**
     * calls onRotationChange method in all instances of MobileView
     * Callback function invoked onOrientationChange
     */
    protected static callOrientationChange(): void {
        for (const instance of MobileView._instances) {
            instance.onRotationChange();
        }
    }

    /**
     * Contains all MobileView instances. Used in callOrientationChange function
     * @type MobileView[]
     */
    private static _instances: MobileView[] = new Array();
    private static _currentOrientation: DeviceOrientation = null;
    private static _isListening: boolean = false;

    /**
     * Class constructor. Attaches orientation change listener and adds instance to _instances array.
     */
    constructor() {
        super();
   
        if (!MobileView._isListening) {
            MobileView.listenForOrientationChange();
            MobileView._isListening = true;
        }

        MobileView._instances.push(this);
    }

    /**
     * @deprecated
     * dispose should be used instead of this method for consistency and also dispose
     * calls the same method on its children so that the method should not be overridden
     * just too loop through the children and call removeInstance on them manually.
     */
    removeInstance() : MobileView {
        let index = MobileView._instances.findIndex(instance => instance === this)
        if (index > -1) {
            return MobileView._instances.splice(index, 1)[0]
        }
    }

    /**
     * Removes an instance from the static array of MobileView instances
     * Call this function prior deletion of a MobileView object
     * @returns the removed instance
     */
    dispose() : void {
        let index = MobileView._instances.findIndex(instance => instance === this)
        if (index > -1) {
            const instance = MobileView._instances.splice(index, 1)[0]

            instance.children.forEach((child: MobileView) => {
                if ('dispose' in child) {
                    child.dispose()
                }
            })
        }
    }

    /**
     * callback function invoked on rotation event
     * @abstract
     */
    protected onRotationChange(): void {}
}

/**
 * An interface to represent a point in the canvas. Same as PIXI.IPointData interface but gives the opportunity
 * to be used without including the PIXI itself.
 */
export interface Point {
    x: number
    y: number
}
